import { DataTableDataType } from "best-common-react";
import { EyeballEntityType, EyeballPlayState } from "../constants/EyeballConstants";

export type SessionAuthenticator = {
  sessionAuthenticatorId: number;
  authenticatorId: number;
  authenticatorFirstName: string;
  authenticatorLastName: string;
  numHolograms: number;
  status: string;
  isSessionClosed: boolean;
  isSessionPrePublished: boolean;
  isSessionAdminReviewComplete: boolean;
  isSessionAdminReviewCompleteAndPublishAll: boolean;
};

export type SessionAuthenticatorTrackviaHologramCount = {
  sessionAuthenticatorId: number;
  numHologramsInTrackvia: number;
};

export type FileDTO = {
  id: number;
  name: string;
};

export type SessionsPayload = {
  sessions: Session[];
  totalSessions: number;
};

export interface GameSession extends Session {
  gamePk: number;
  awayTeamId: number;
  homeTeamId: number;
  awayTeamName: string;
  homeTeamName: string;
  doubleHeader: boolean;
  gameNumber: number;
}

export interface Session extends DataTableDataType {
  type: SessionType;
  sessionName: string;
  sessionId: number;
  sessionRecordId: number;
  dateTime: string;
  sessionAuthenticators: SessionAuthenticator[];
  numClosedSessions: number;
  numPrepublishedSessions: number;
  numAdminReviewCompleteSessions: number;
  numAllItemsPublishedSessions: number;
}

export enum SessionType {
  GAME = "Game",
  PUBLIC = "Public",
  PRIVATE = "Private",
}

export type EyeballAdminSessionPayload = {
  adminSession: Session;
  eyeballSessionAuthenticatorInfos: Record<number, EyeballSessionAuthenticatorInfo>;
};

export type AuthenticatorItemReview = {
  sessionAuthenticator: SessionAuthenticator;
  review: EyeballItemReview;
};

export type AuthenticatorOtherItemReview = {
  sessionAuthenticator: SessionAuthenticator;
  review: EyeballOtherItemReview;
};

export interface EyeballSessionAuthenticatorInfo extends DataTableDataType {
  sessionAuthenticatorId: number;
  authenticatorId: number;
  authenticatorFirstName: string;
  authenticatorLastName: string;
  itemReviews: EyeballItemReview[];
  otherItemReviews: EyeballOtherItemReview[];
  totalAuthenticated: number;
  areAuthenticatedItemsWithMissingPlaysPresent: boolean;
  areAuthenticatedItemsWithFlagPresent: boolean;
  areAuthenticatedBallsWithPitchOfInterestNotLastPresent: boolean;
  reviewStatus: string;
  isPrePublished: boolean;
  isAdminReviewComplete: boolean;
  areAllItemsPublished: boolean;
  hologramRolls: EyeballHologramRoll[];
  destroyedHolograms: EyeballDestroyedHologram[];
  closeSession: boolean;
}

export interface DuplicateInfo {
  authenticatorName: string;
  hologram: string;
  itemId: number;
}

export interface EyeballReview {
  itemId: number;
  entityType: EyeballEntityType;
  itemType?: string;
  hologram?: string;
  secondHologram?: string;
  firstBatPiece?: string;
  secondBatPiece?: string;
  prevAuth?: boolean;
  sessionAuthenticatorId: number;
  flagged: boolean;
  flaggedDate?: string;
  reviewed: boolean;
  reviewedDate?: string;
  description?: string;
  comment?: string;
  hasComment: boolean;
  itemOrder: number;
  duplicateItems: DuplicateInfo[];
  isDuplicate: boolean;
}

export interface EyeballItemReview extends EyeballReview {
  isIncompleteBat: boolean;
  matchedAtBatPlayGroups: MatchedAtBatPlayGroup[];
  // NOTE: these lists intentionally contain null
  eyeballPlays: (EyeballPlay | null)[];
  mlbPlays: (MlbPlay | null)[];
  arePlaysMismatched: boolean;
  isAnyPitchOfInterestNotLastForBall: boolean;
}

export interface EyeballOtherItemReview extends EyeballReview {
  appId: string;
  itemCategory?: string;
  otherItemType: string;
  barcode?: string;
  prevAuth?: boolean;
  uniformType?: string;
  uniformColor?: string;
  autographers?: string;
  description?: string;
  playerId?: number;
  playerName?: string;
}

export type MatchedAtBatPlayGroup = {
  atBatIndex: number | null;
  atBatResult: string | null;
  matchedAtBatPlays: MatchedAtBatPlay[];
};

export type MatchedAtBatPlay = {
  eyeballPlay: EyeballPlay | null;
  mlbPlay: MlbPlay | null;
  nonPitchEventMismatch: boolean;
};

export type EyeballPlay = {
  id: number;
  itemType: string;
  itemCategory: string;
  inning: number;
  halfInning: string;
  playerId: number;
  playerName: string;
  playerJerseyNumber: number;
  hologram: string;
  secondHologram: string;
  state: EyeballPlayState;
  atBatIndex: number;
  playEventIndex: number;
  pitchIndex: number;
  eventType: string;
  nonPitchEvent: boolean;
};

export type MlbPlay = {
  playId: string;
  gamePk: number;
  playIndex: PlayIndex;
  eyeballPlayIndex: number;
  pitchNumber: number | null;
  batterId: number;
  batterName: string;
  pitcherId: number;
  pitcherName: string;
  inning: number;
  halfInning: string;
  postPlayCount: PlayCount;
  hitData: HitData;
  playResult: string;
  atBatResult: string;
  nonPitchEvent: boolean;
  nonPitchEventInfo?: NonPitchEventInfo;
  pitchOfInterest: boolean;
  pitchViolation: boolean;
};

export type PlayIndex = {
  atBatIndex: number;
  playIndex: number;
};

export type PlayCount = {
  balls: number;
  strikes: number;
  outs: number;
};

export type HitData = {
  exitVelocity: number;
  launchAngle: number;
  distance: number;
};

export type NonPitchEventInfo = {
  actionPlayId: string;
  description: string;
  event: string;
  eventType: string;
  playIndex: PlayIndex;
};

export type EyeballHologramRoll = {
  id: number;
  sessionAuthenticatorId: number;
  prefix: string;
  number: number;
  startingHologramId: number;
  endingHologramId: number;
  nextHologramId: number;
};

export type EyeballDestroyedHologram = {
  rollId: number;
  hologramId: number;
};

export type EyeballDiamondVideo = {
  angle: string;
  playStartOffset: number;
  url: string;
};

export type EyeballDiamondVideos = {
  videos: Map<string, EyeballDiamondVideo>;
};

export enum PlayEventTypeName {
  PITCH = "PITCH",
  PICKOFF_ATTEMPT = "PICKOFF_ATTEMPT",
  PITCH_VIOLATION = "PITCH_VIOLATION",
}

export type EyeballPlayType = "eyeball" | "mlb";

export const EyeballPlayType = {
  EYEBALL: "eyeball" as EyeballPlayType,
  MLB: "mlb" as EyeballPlayType,
};

export enum ItemReviewStatus {
  ITEM_INCOMPLETE = "itemIncomplete",
  FLAG = "flag",
  PITCH_OF_INTEREST_NOT_LAST = "pitchOfInterestNotLast",
  HAS_COMMENT = "hasComment",
  DUPLICATE = "duplicate",
}

export enum PostMessageAction {
  READY = "ready",
  SYNCING = "syncing",
  REFRESH = "refresh",
  ERROR = "error",
  EDIT_STARTED = "edit_started",
}

export enum EyeballPostMsgType {
  AUTH = "auth",
  ADD_ITEMS = "add_items",
  TRAINING = "training",
  EDIT = "edit",
  RESET_EDIT_MODE = "reset_edit_mode",
}

export enum EyeballTrainingType {
  CANNED = "canned",
  LIVE = "live",
  REVIEW = "review",
}

export type AuthInfo = {
  accessToken: string;
  accessTokenIssuedAtInSeconds: number;
  accessTokenExpiresInSeconds?: number;
  refreshToken: string;
  email: string;
};

export type IframePostMsgBody = {
  type: EyeballPostMsgType;
  authInfo: AuthInfo;
  sessionAuthenticatorId?: number;
  sessionId?: number;
  gamePk?: number;
  editItemId?: number;
  scrollToEntityType?: EyeballEntityType;
  scrollToIndex?: number;
  training?: EyeballTrainingType;
};

export type EnterEditModePayload = Required<
  Pick<
    IframePostMsgBody,
    "sessionAuthenticatorId" | "sessionId" | "editItemId" | "scrollToEntityType" | "scrollToIndex"
  >
> &
  Pick<IframePostMsgBody, "training" | "gamePk">;

export type AddItemsPayload = Required<Pick<IframePostMsgBody, "sessionAuthenticatorId" | "sessionId">> &
  Pick<IframePostMsgBody, "training" | "gamePk">;

export interface ErrorPostMessage {
  action: PostMessageAction.ERROR;
  errorMessage: string;
  errorType?: "conflict" | "sync" | "general";
}
